import React, { useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, ArrowLeft, RotateCcw, MessageCircle, Home, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';

export default function Failed() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscription, refreshSubscription } = useSubscription();
  const sessionId = searchParams.get('session_id');
  const { t, i18n } = useTranslation();
  const { isRTL } = useTheme();

  // Helper function to get translated field values
  const getTranslatedField = (fallback, translationsObj) => {
    if (!translationsObj || typeof translationsObj !== 'object') {
      return fallback;
    }
    return translationsObj[i18n.language] || translationsObj.en || fallback;
  };

  useEffect(() => {
    // Refresh subscription data once on mount
    refreshSubscription();
  }, []); // Empty dependency array ensures this runs only once
  const reasons = [
    t('paymentFailed.reasonWindowClosed', 'Payment window was closed before completion'),
    t('paymentFailed.reasonDeclined', 'Payment method declined or insufficient funds'),
    t('paymentFailed.reasonNetwork', 'Network connection interrupted'),
    t('paymentFailed.reasonChangedMind', "Changed your mind (that's okay!)")
  ];

  const handleTryAgain = () => {
    navigate('/#pricing');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@cvlogs.com?subject=Payment Issue - Session: ' + (sessionId || 'N/A');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" dir={isRTL ? 'rtl' : 'ltr'}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl w-full"
      >
        {/* Failed Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with animated X icon */}
          <div className="relative bg-[#2F80ED] px-8 py-12 text-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.2
              }}
              className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-4 shadow-lg"
            >
              <XCircle className="w-14 h-14 text-slate-600" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-4xl font-black text-white mb-2"
            >
              {t('paymentFailed.title', 'Payment Cancelled')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-blue-50 text-lg"
            >
              {t('paymentFailed.subtitle', 'Your subscription was not activated')}
            </motion.p>
          </div>

          {/* Content */}
          <div className="px-8 py-10">
            {/* Information */}
            <div className="mb-8 text-center">
              <p className="text-slate-700 text-lg mb-4">
                {t('paymentFailed.noChargeMessage', "Don't worry! No charges were made to your account.")}
              </p>
              <p className="text-slate-600">
                {t('paymentFailed.cancellationReason', 'Your payment was cancelled or interrupted. This can happen for various reasons:')}
              </p>
            </div>

            {/* Reasons List */}
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <ul className="space-y-3 text-slate-700">
                {reasons.map((reason, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              {/* Try Again - Primary */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleTryAgain}
                className="w-full bg-[#2F80ED] hover:bg-[#1E6FCC] text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <RotateCcw className="w-5 h-5" />
                {t('paymentFailed.tryAgain', 'Try Again')}
              </motion.button>

              {/* Secondary Actions */}
              <div className="grid grid-cols-2 gap-4">
                {/* Go Home */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGoHome}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
                >
                  <Home className="w-4 h-4" />
                  {t('paymentFailed.goHome', 'Go Home')}
                </motion.button>

                {/* Contact Support */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleContactSupport}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all duration-300"
                >
                  <MessageCircle className="w-4 h-4" />
                  {t('paymentFailed.support', 'Support')}
                </motion.button>
              </div>
            </div>

            {/* Still Want to Subscribe */}
            <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 mb-2">{t('paymentFailed.upgradeTitle', 'Still interested in upgrading?')}</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {t('paymentFailed.upgradeDescription', 'Our premium plans unlock powerful features to accelerate your career journey. Choose a plan that fits your needs.')}
                  </p>
                  <Link
                    to="/#pricing"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm transition-colors"
                  >
                    {t('paymentFailed.viewPlans', 'View all plans')}
                    <ArrowLeft className="w-4 h-4 rotate-180" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Session ID (for debugging) */}
            {sessionId && (
              <p className="text-xs text-slate-400 text-center mt-6">
                {t('paymentFailed.sessionId', 'Session ID')}: {sessionId}
              </p>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-8"
        >
          <div className="bg-white/80 backdrop-blur rounded-xl px-6 py-4 inline-block shadow-sm">
            <p className="text-slate-600 text-sm mb-2">
              {t('paymentFailed.needHelp', 'Need help with payment?')}
            </p>
            <a
              href="mailto:support@cvlogs.com"
              className="text-blue-600 hover:underline font-medium text-sm"
            >
              {t('paymentFailed.contactSupport', 'Contact Support: support@cvlogs.com')}
            </a>
          </div>
        </motion.div>

        {/* Free Plan Reminder */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center mt-6"
        >
          <p className="text-slate-500 text-sm">
            {t('paymentFailed.freePlanReminder', 'You can still use CVLogs with our')}{' '}
            <Link to="/" className="text-blue-600 hover:underline font-medium">
              {t('paymentFailed.freePlanLink', 'free plan')}
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
